import type { ECharts, EChartsOption, SeriesOption } from 'echarts'
import { filter, isPlainObject, last, map, pipe } from 'remeda'
import { createIcon } from '../helpers/icon'
import { captureEffectError, pushAndCall, pushEvent } from '../helpers/utils'

const CHART_FONT = 'Keruj Mono, monospace'

// Each chart owns only its latest unfinished render.
const chartRenders = new WeakMap<HTMLElement, AbortController>()

const TOOLTIP_STYLE = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderColor: '#e5e7eb',
  borderWidth: 1,
  extraCssText: `box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 6px; font-family: ${CHART_FONT};`,
  textStyle: {
    fontFamily: CHART_FONT,
    fontSize: 12,
    color: '#1f2937'
  }
} as const

// Type for ECharts event parameters
interface ChartEventParams {
  name?: string
  value?: unknown
  seriesName?: string
  seriesType?: string
  dataIndex?: number
  seriesIndex?: number
  componentType?: string
  percent?: number
  selected?: unknown[]
  data?: {
    name?: string
    value?: unknown
    originalValue?: unknown
    [key: string]: unknown
  }
  [key: string]: unknown
}

// Type for tooltip formatter parameters
interface TooltipFormatterParams {
  name?: string
  value?: unknown
  seriesName?: string
  seriesType?: string
  dataIndex?: number
  seriesIndex?: number
  componentType?: string
  percent?: number
  marker?: string
  color?: string | { colorStops?: unknown[] }
  data?: {
    name?: string
    value?: unknown
    [key: string]: unknown
  }
  [key: string]: unknown
}

// Type for tooltip configuration objects
interface TooltipConfig {
  readonly trigger?: string
  readonly valueField?: string
  readonly context_details?: Record<string, unknown>
  readonly axisPointer?: Record<string, unknown>
  readonly show_total?: boolean
  readonly total_label?: string
  readonly currency?: string
  readonly locale?: string
  readonly show_cents?: boolean
  [key: string]: unknown
}

type CurrencyTooltipFormat = Readonly<{
  currency: string
  locale: string
  fractionDigits: 0 | 2
}>

type TooltipRecord = Readonly<Record<string, unknown>>
type TooltipPositionSize = Readonly<{
  contentSize?: readonly number[]
}>
type TooltipPosition = (
  point: readonly number[],
  params: unknown,
  dom: HTMLElement,
  rect: unknown,
  size: TooltipPositionSize
) => [number, number]
type BodyTooltipOptions = Readonly<{
  appendTo: (chartContainer: HTMLElement) => HTMLElement
  position: TooltipPosition
}>

const objectHasOwnProperty = Object.prototype.hasOwnProperty
const TOOLTIP_VIEWPORT_PADDING = 8
const TOOLTIP_POINTER_OFFSET = 12

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max))

const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

const escapeHtmlText = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const nonEmptyStringOr = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() !== '' ? value : fallback

/** Grid heatmap tooltip line: the value section's label, the value and its unit. */
const heatmapValueLine = (tooltip: TooltipConfig, value: unknown): string => {
  const sections = Array.isArray(tooltip.sections) ? tooltip.sections : []
  const valueSection: unknown = sections[2]
  const label = nonEmptyStringOr(
    isPlainObject(valueSection) ? valueSection.label : undefined,
    'Value'
  )
  const suffix = nonEmptyStringOr(tooltip.value_suffix, '')

  return escapeHtmlText(`${label}: ${String(value)}${suffix}`)
}

const currencyTooltipFormat = (
  tooltip: TooltipConfig,
  fallbackLocale: string
): CurrencyTooltipFormat => ({
  currency: nonEmptyStringOr(tooltip.currency, 'USD').trim().toUpperCase(),
  locale: nonEmptyStringOr(tooltip.locale, fallbackLocale),
  fractionDigits: tooltip.show_cents === false ? 0 : 2
})

const formatCurrencyTooltipValue = (
  value: number,
  format: CurrencyTooltipFormat
): string => {
  try {
    return new Intl.NumberFormat(format.locale, {
      style: 'currency',
      currency: format.currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: format.fractionDigits,
      maximumFractionDigits: format.fractionDigits
    }).format(value)
  } catch {
    return `${value.toLocaleString()} ${format.currency}`
  }
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const tooltipNumericValue = (value: unknown): number | null =>
  isFiniteNumber(value)
    ? value
    : Array.isArray(value)
      ? (pipe(value, filter(isFiniteNumber), last()) ?? null)
      : null

const currencyTooltipRow = (
  item: TooltipFormatterParams,
  format: CurrencyTooltipFormat
): string | null => {
  const value = tooltipNumericValue(item.value)
  const trustedMarker = item.marker ?? ''
  const seriesName = escapeHtmlText(item.seriesName ?? '')

  return value === null
    ? null
    : `<div class="chart-tooltip-row"><span class="chart-tooltip-label">${trustedMarker}${seriesName}:</span><span class="chart-tooltip-value">${formatCurrencyTooltipValue(value, format)}</span></div>`
}

const currencyTooltipRows = (
  params: readonly TooltipFormatterParams[],
  format: CurrencyTooltipFormat
): string =>
  pipe(
    params,
    map((item) => currencyTooltipRow(item, format)),
    filter((row): row is string => row !== null)
  ).join('')

export const removeChartLoadingOverlay = (chart: HTMLElement): void => {
  chart.querySelector('[data-part="loading-overlay"]')?.remove()
}

// Cancel older work before waiting for the engine or fonts.
const beginChartRender = (element: HTMLElement): AbortSignal => {
  chartRenders.get(element)?.abort()
  const controller = new AbortController()
  chartRenders.set(element, controller)

  return controller.signal
}

const isTooltipRecord = (value: unknown): value is TooltipRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const hasOwnTooltipOption = (tooltip: TooltipRecord, key: string): boolean =>
  objectHasOwnProperty.call(tooltip, key)

const shouldAppendTooltipToBody = (tooltip: TooltipRecord): boolean =>
  tooltip.confine !== true &&
  tooltip.renderMode !== 'richText' &&
  !hasOwnTooltipOption(tooltip, 'appendTo') &&
  !hasOwnTooltipOption(tooltip, 'appendToBody')

const createBodyTooltipOptions = (): BodyTooltipOptions => {
  let chartContainer: HTMLElement | null = null

  const appendTo = (container: HTMLElement): HTMLElement => {
    chartContainer = container

    return document.body
  }

  const position: TooltipPosition = (point, _params, dom, _rect, size) => {
    const chartRect = chartContainer?.getBoundingClientRect()
    const chartLeft = chartRect?.left ?? 0
    const chartTop = chartRect?.top ?? 0
    const tooltipWidth = finiteOr(size.contentSize?.[0], dom.offsetWidth)
    const tooltipHeight = finiteOr(size.contentSize?.[1], dom.offsetHeight)
    const viewportPointX = chartLeft + finiteOr(point[0], 0)
    const viewportPointY = chartTop + finiteOr(point[1], 0)

    const preferredRight = viewportPointX + TOOLTIP_POINTER_OFFSET
    const preferredLeft = viewportPointX - tooltipWidth - TOOLTIP_POINTER_OFFSET
    const preferredX =
      preferredRight + tooltipWidth + TOOLTIP_VIEWPORT_PADDING <=
      window.innerWidth
        ? preferredRight
        : preferredLeft

    const preferredAbove =
      viewportPointY - tooltipHeight - TOOLTIP_POINTER_OFFSET
    const preferredBelow = viewportPointY + TOOLTIP_POINTER_OFFSET
    const preferredY =
      preferredAbove >= TOOLTIP_VIEWPORT_PADDING
        ? preferredAbove
        : preferredBelow

    const viewportX = clamp(
      preferredX,
      TOOLTIP_VIEWPORT_PADDING,
      window.innerWidth - tooltipWidth - TOOLTIP_VIEWPORT_PADDING
    )
    const viewportY = clamp(
      preferredY,
      TOOLTIP_VIEWPORT_PADDING,
      window.innerHeight - tooltipHeight - TOOLTIP_VIEWPORT_PADDING
    )

    return [viewportX - chartLeft, viewportY - chartTop]
  }

  return { appendTo, position }
}

const withBodyTooltipAppendTarget = (tooltip: unknown): unknown => {
  if (!(isTooltipRecord(tooltip) && shouldAppendTooltipToBody(tooltip))) {
    return tooltip
  }

  const bodyTooltipOptions = createBodyTooltipOptions()

  return {
    ...tooltip,
    appendTo: bodyTooltipOptions.appendTo,
    ...(hasOwnTooltipOption(tooltip, 'position')
      ? {}
      : { position: bodyTooltipOptions.position })
  }
}

/**
 * Custom error classes for chart operations with Sentry integration
 */
class ChartInitError extends Error {
  constructor(
    public chartId: string,
    message: string,
    public cause?: unknown
  ) {
    super(message)
    this.name = 'ChartInitError'
  }
}

class ChartRenderError extends Error {
  constructor(
    public chartId: string,
    message: string,
    public cause?: unknown
  ) {
    super(message)
    this.name = 'ChartRenderError'
  }
}

class ChartDataError extends Error {
  constructor(
    public chartId: string,
    message: string,
    public cause?: unknown
  ) {
    super(message)
    this.name = 'ChartDataError'
  }
}

/**
 * Chart Hook for rendering ECharts specifications
 *
 * This hook integrates with Phoenix LiveView to render interactive charts
 * using ECharts specifications generated on the server side.
 */
export const FacadeChart = {
  mounted() {
    try {
      // Check if this is a re-mount (chart was previously mounted)
      const isRemount = this.el.dataset.chartMounted === 'true'
      this.el.dataset.chartMounted = 'true'

      // Store remount state for use in renderChart
      this.isRemount = isRemount

      // Get the ECharts specification from data attribute
      const specString = this.el.dataset.spec

      if (!specString) {
        const error = new ChartDataError(
          this.el.id,
          'No chart specification provided'
        )
        captureEffectError(error, {
          operation: 'chart:mounted',
          payload: { chartId: this.el.id }
        })
        this.showError('No chart specification provided')
        return
      }

      let spec: EChartsOption
      try {
        spec = JSON.parse(specString)
      } catch (parseError) {
        const error = new ChartDataError(
          this.el.id,
          'Invalid chart specification JSON',
          parseError
        )
        captureEffectError(error, {
          operation: 'chart:mounted:parse',
          payload: {
            chartId: this.el.id,
            spec: specString.substring(0, 200) // First 200 chars for debugging
          }
        })
        this.showError('Invalid chart specification', parseError)
        return
      }

      // Validate spec has minimum required properties
      if (!spec || typeof spec !== 'object') {
        const error = new ChartDataError(
          this.el.id,
          'Chart specification must be an object'
        )
        captureEffectError(error, {
          operation: 'chart:mounted:validate',
          payload: {
            chartId: this.el.id,
            specType: typeof spec
          }
        })
        this.showError('Chart specification must be an object')
        return
      }

      // Render the chart
      this.renderChart(spec)
    } catch (error) {
      const chartError = new ChartInitError(
        this.el.id,
        'Failed to initialize chart',
        error
      )
      captureEffectError(chartError, {
        operation: 'chart:mounted:fatal',
        payload: { chartId: this.el.id }
      })
      this.showError('Failed to initialize chart', error)
    }

    // Handle updates from server
    this.handleEvent(
      `chart:update:${this.el.id}`,
      (payload: { spec: string }) => {
        try {
          const newSpec = JSON.parse(payload.spec)
          this.renderChart(newSpec)
        } catch (_error) {}
      }
    )

    // Handle resize events for responsive charts
    this.resizeObserver = new ResizeObserver(() => {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    })

    this.resizeObserver.observe(this.el)

    // Handle chart interactions
    this.handleChartInteractions()
  },

  async renderChart(spec: EChartsOption): Promise<void> {
    const signal = beginChartRender(this.el)

    try {
      const [echarts] = await Promise.all([
        import('./chart-engine'),
        document.fonts.ready
      ])

      if (signal.aborted) {
        return
      }

      // Remove the initial loading overlay before ECharts owns the container.
      removeChartLoadingOverlay(this.el)

      // Dispose existing chart instance if it exists
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }

      // Create or get container for the chart
      let container = this.el.querySelector(
        '.echarts-container'
      ) as HTMLDivElement
      if (!container) {
        container = document.createElement('div')
        container.className = 'echarts-container w-full h-full'
        // Make container fully responsive to parent
        container.style.width = '100%'
        container.style.height = '100%'
        container.style.position = 'relative'
        this.el.appendChild(container)
      }

      // Get locale from tooltip or document
      const locale = this.detectChartLocale()

      // Initialize ECharts instance with locale
      this.chartInstance = echarts.init(container, null, {
        locale: locale === 'uk' ? 'uk' : undefined // Use Ukrainian locale if specified, otherwise default
      })

      // Parse function strings back to actual functions
      const processedSpec = this.processFunctionStrings(spec)

      // Validate and fix series
      if (processedSpec.series) {
        if (!Array.isArray(processedSpec.series)) {
          // Capture warning to Sentry for monitoring
          captureEffectError(
            new ChartDataError(
              this.el.id,
              'Non-array series detected, converting to array'
            ),
            {
              operation: 'chart:renderChart:series',
              payload: {
                chartId: this.el.id,
                seriesType: typeof processedSpec.series
              }
            }
          )

          // If series is an object with chart type keys (from theme), ignore it
          if (
            typeof processedSpec.series === 'object' &&
            (processedSpec.series.bar || processedSpec.series.line)
          ) {
            processedSpec.series = []
          } else {
            // Convert single series to array
            processedSpec.series = [processedSpec.series]
          }
        }

        // Filter out invalid entries
        processedSpec.series = processedSpec.series.filter(
          (s: SeriesOption) => {
            if (!s || s === undefined || s === null) {
              return false
            }
            return true
          }
        )
      } else {
        // No series defined, use empty array
        processedSpec.series = []
      }

      // Set chart option with error handling
      try {
        // Disable animations on re-mount to prevent re-animation when details toggle
        if (this.isRemount) {
          processedSpec.animation = false
        }

        this.chartInstance.setOption(processedSpec)

        // Apply selection (pulled-out pie slice / bordered stacked bar) once
        // the chart has its data loaded.
        this.applySelectionFromDataset()

        const onClick = this.el.dataset.onClick
        if (onClick) {
          this.setupClickHandler(onClick)
        }

        // Notify server that chart is ready - fire and forget is OK here
        const target = this.el.getAttribute('phx-target') || undefined
        pushEvent(this, 'chart_ready', { id: this.el.id }, target)
      } catch (setOptionError) {
        const error = new ChartRenderError(
          this.el.id,
          'Error setting chart option',
          setOptionError
        )
        captureEffectError(error, {
          operation: 'chart:renderChart:setOption',
          payload: {
            chartId: this.el.id,
            seriesCount: processedSpec.series?.length || 0
          }
        })
        throw setOptionError
      }
    } catch (error) {
      if (signal.aborted) {
        return
      }

      const renderError = new ChartRenderError(
        this.el.id,
        'Failed to render chart',
        error
      )
      captureEffectError(renderError, {
        operation: 'chart:renderChart:fatal',
        payload: { chartId: this.el.id }
      })
      this.showError('Failed to render chart', error)
    }
  },

  setupClickHandler(eventName: string) {
    // Bind once to the newly rendered instance, including after a server update.
    if (this.chartInstance) {
      this.chartInstance.on('click', (params: ChartEventParams) => {
        // Prepare data based on chart type
        let clickData: Record<string, unknown> = {}

        // Check if it's a heatmap (value is an array)
        if (Array.isArray(params.value) && params.value.length === 3) {
          // Heatmap: value is [x_index, y_index, value]
          // Get the actual axis labels from the chart option
          const option = this.chartInstance.getOption()
          const xAxis = option.xAxis?.[0]
          const yAxis = option.yAxis?.[0]

          const valueArray = params.value as [number, number, number]
          const xIndex = valueArray[0]
          const yIndex = valueArray[1]
          const cellValue = valueArray[2]

          // Get actual labels (hour and day)
          const hour = xAxis?.data?.[xIndex] || xIndex
          const day = yAxis?.data?.[yIndex] || yIndex

          clickData = {
            hour: hour,
            day: day,
            count: cellValue
          }
        }
        // Check if it's a pie chart
        else if (params.seriesType === 'pie') {
          // Pie chart: params.data contains the pie segment data
          // For radial progress, we need the name field
          const pieData = params.data || {}

          clickData = {
            name: pieData.name || params.name,
            category: pieData.name || params.name,
            value: pieData.originalValue || pieData.value || params.value,
            percentage: params.percent || 0,
            dataIndex: params.dataIndex,
            seriesIndex: params.seriesIndex
          }
        }
        // Default case for other chart types
        else {
          clickData = {
            name: params.name,
            value: params.value,
            seriesName: params.seriesName,
            dataIndex: params.dataIndex,
            seriesIndex: params.seriesIndex
          }
        }

        // Send click event to server with custom event name
        // Get the target from phx-target attribute if it exists
        const target = this.el.getAttribute('phx-target') || undefined

        // Use pushAndCall for click events to ensure delivery
        pushAndCall(
          this,
          eventName,
          {
            id: this.el.id,
            data: clickData
          },
          () => {}, // No-op callback since we don't need the response
          target,
          'Chart click event failed'
        )
      })
    }
  },

  handleChartInteractions() {
    // Click handler is already set up via setupClickHandler if on_click attribute is provided
    // This method now only handles hover and selection events

    // Handle hover events
    this.handleEvent(`chart:on_hover:${this.el.id}`, () => {
      if (this.chartInstance) {
        this.chartInstance.on('mouseover', (params: ChartEventParams) => {
          pushEvent(this, 'chart_hover', {
            id: this.el.id,
            data: {
              name: params.name,
              value: params.value,
              seriesName: params.seriesName,
              dataIndex: params.dataIndex,
              seriesIndex: params.seriesIndex
            }
          })
        })
      }
    })

    // Handle selection events
    this.handleEvent(`chart:enable_selection:${this.el.id}`, () => {
      if (this.chartInstance) {
        // ECharts handles selection differently, we can use brush or data selection
        this.chartInstance.on('selectchanged', (params: ChartEventParams) => {
          pushEvent(this, 'chart_selection', {
            id: this.el.id,
            selection: params.selected
          })
        })
      }
    })
  },

  /**
   * Sync the chart's selection state with `data-selected-data-index` /
   * `data-selected-series-index` set by the server. LiveView updates the
   * dataset attributes even with `phx-update="ignore"`, so this is the
   * declarative path: server picks the index, client reflects it.
   */
  updated() {
    this.applySelectionFromDataset()
  },

  applySelectionFromDataset() {
    if (!this.chartInstance) {
      return
    }

    const rawDataIndex = this.el.dataset.selectedDataIndex
    const rawSeriesIndex = this.el.dataset.selectedSeriesIndex
    const dataIndex =
      rawDataIndex === undefined || rawDataIndex === ''
        ? null
        : Number(rawDataIndex)
    const seriesIndex =
      rawSeriesIndex === undefined || rawSeriesIndex === ''
        ? null
        : Number(rawSeriesIndex)

    // ECharts treats `selectedMode: 'single'` as one selection PER SERIES,
    // so a stacked bar with N series can carry N highlights at once. Clear
    // every series × dataIndex pair explicitly — `unselect` with only
    // `seriesIndex` is unreliable across versions, but the (series, data)
    // form always works.
    const option = this.chartInstance.getOption() as {
      series?: { data?: unknown[] }[]
    }
    if (Array.isArray(option.series)) {
      option.series.forEach((s, sIdx) => {
        if (Array.isArray(s.data)) {
          s.data.forEach((_item, dIdx) => {
            this.chartInstance.dispatchAction({
              type: 'unselect',
              seriesIndex: sIdx,
              dataIndex: dIdx
            })
          })
        }
      })
    }

    if (dataIndex === null || Number.isNaN(dataIndex)) {
      return
    }

    this.chartInstance.dispatchAction({
      type: 'select',
      seriesIndex:
        seriesIndex === null || Number.isNaN(seriesIndex) ? 0 : seriesIndex,
      dataIndex
    })
  },

  destroyed() {
    chartRenders.get(this.el)?.abort()
    chartRenders.delete(this.el)

    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    // Clean up ECharts instance
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },

  /**
   * Process ECharts specification to handle structured tooltip data
   * Converts data-driven tooltip configurations into ECharts formatter functions
   */
  processFunctionStrings(spec: unknown): unknown {
    if (!spec || typeof spec !== 'object') {
      return spec
    }

    // Create a shallow copy to avoid mutating the original
    const processed = { ...spec } as Record<string, unknown>

    // Process calendar configurations to add properly ordered day names
    if (processed.calendar) {
      processed.calendar = this.processCalendarDayNames(processed.calendar)
    }

    // Process tooltip if it exists
    if (processed.tooltip) {
      processed.tooltip = withBodyTooltipAppendTarget(
        this.processTooltip(processed.tooltip)
      )
    }

    return processed
  },

  /**
   * Process calendar configuration to ensure correct day name mapping
   */
  processCalendarDayNames(calendar: unknown): unknown {
    const processCalendarItem = (cal: Record<string, unknown>) => {
      if (!cal || typeof cal !== 'object') {
        return cal
      }

      // ECharts expects nameMap to ALWAYS be in Sunday-first order: [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
      // regardless of the firstDay setting. The firstDay only controls visual arrangement.
      if (cal.dayLabel) {
        const locale = this.detectChartLocale()

        const dayLabelWithNameMap = cal.dayLabel as { nameMap?: string[] }
        if (locale === 'uk') {
          // Sunday-first order for Ukrainian (ECharts standard)
          // [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
          dayLabelWithNameMap.nameMap = [
            'Нд',
            'Пн',
            'Вт',
            'Ср',
            'Чт',
            'Пт',
            'Сб'
          ]
        } else {
          // Sunday-first order for English (ECharts standard)
          // [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
          dayLabelWithNameMap.nameMap = [
            'Su',
            'Mo',
            'Tu',
            'We',
            'Th',
            'Fr',
            'Sa'
          ]
        }
      }

      return cal
    }

    // Process single or multiple calendars
    if (Array.isArray(calendar)) {
      return calendar.map(processCalendarItem)
    }
    return processCalendarItem(calendar as Record<string, unknown>)
  },

  /**
   * Process radial progress tooltip
   */
  processRadialProgressTooltip(tooltipObj: TooltipConfig) {
    const valueField = tooltipObj.valueField || 'value'
    return {
      ...TOOLTIP_STYLE,
      trigger: tooltipObj.trigger || 'item',
      formatter: (params: TooltipFormatterParams) => {
        const value = params.data?.[valueField] || params.value || 0
        return `${params.name}: ${value}`
      }
    }
  },

  /**
   * Process heatmap with context tooltip
   */
  processHeatmapWithContextTooltip(tooltipObj: TooltipConfig) {
    const contextDetails = tooltipObj.context_details || {}
    return {
      ...TOOLTIP_STYLE,
      trigger: tooltipObj.trigger || 'item',
      formatter: (params: TooltipFormatterParams) => {
        const valueArray = Array.isArray(params.value) ? params.value : [0, 0]
        const xValue = valueArray[0]
        const yValue = valueArray[1]

        // Get axis data from the chart instance
        const chartOption = this.chartInstance?.getOption() || {}
        const xData = chartOption.xAxis?.[0]?.data || []
        const yData = chartOption.yAxis?.[0]?.data || []

        const dayShort = yData[yValue] || yValue
        const hour = xData[xValue] || xValue

        // Use the day abbreviation as-is (already localized from server)
        const dayFull = dayShort

        // Format hour with :00 suffix
        const hourFormatted =
          typeof hour === 'number'
            ? `${hour}:00`
            : typeof hour === 'string' && /^\d{1,2}$/.test(hour)
              ? `${hour}:00`
              : hour

        // Build tooltip content
        let content = `<div class="chart-tooltip" style="font-family: ${CHART_FONT};">`
        content += `<div class="chart-tooltip-title">${dayFull}, ${hourFormatted}</div>`

        // Add context breakdown if available
        const key = `${dayShort},${hour}`
        const contexts =
          (contextDetails as Record<string, unknown[]>)[key] || []

        if (contexts && contexts.length > 0) {
          content += '<div class="chart-tooltip-divider">'

          for (const ctx of contexts as Record<string, unknown>[]) {
            const percentage =
              typeof ctx.percentage === 'number'
                ? ctx.percentage.toFixed(1)
                : '0.0'
            content += '<div class="chart-tooltip-row">'
            content += `<span class="chart-tooltip-label">${ctx.context}:</span>`
            content += `<span class="chart-tooltip-value">${percentage}%</span>`
            content += '</div>'
          }

          content += '</div>'
        }

        content += '</div>'
        return content
      }
    }
  },

  /**
   * Process a multi-series axis tooltip whose values share one currency.
   */
  processCurrencyAxisTooltip(tooltipObj: TooltipConfig) {
    const format = currencyTooltipFormat(tooltipObj, this.detectChartLocale())

    return {
      ...TOOLTIP_STYLE,
      trigger: tooltipObj.trigger || 'axis',
      axisPointer: tooltipObj.axisPointer || {},
      formatter: (
        params: TooltipFormatterParams | readonly TooltipFormatterParams[]
      ) => {
        const paramsArray = Array.isArray(params) ? params : [params]
        const title = escapeHtmlText(paramsArray[0]?.name ?? '')

        return `<div class="chart-tooltip" style="font-family: ${CHART_FONT};"><div class="chart-tooltip-title">${title}</div>${currencyTooltipRows(paramsArray, format)}</div>`
      }
    }
  },

  /**
   * Process stacked bar chart tooltip
   */
  processStackedTooltip(tooltipObj: TooltipConfig) {
    const axisPointer = tooltipObj.axisPointer || {}
    const showTotal = tooltipObj.show_total !== false
    const totalLabel = tooltipObj.total_label || 'Total'

    return {
      ...TOOLTIP_STYLE,
      trigger: tooltipObj.trigger || 'axis',
      axisPointer: axisPointer,
      formatter: (params: TooltipFormatterParams) => {
        const paramsArray = Array.isArray(params) ? params : [params]
        let content = `<div class="chart-tooltip" style="font-family: ${CHART_FONT};">`

        // Add the category name (x-axis value)
        if (paramsArray.length > 0) {
          content += `<div class="chart-tooltip-title">${paramsArray[0].name}</div>`
        }

        let total = 0
        const details: string[] = []

        for (const item of paramsArray as Record<string, unknown>[]) {
          const value = Number(item.value) || 0
          if (value > 0) {
            total += value
            details.push(
              `<div class="chart-tooltip-row"><span class="chart-tooltip-label">${item.marker}${item.seriesName}:</span><span class="chart-tooltip-value">${this.formatValue(value, tooltipObj.format as string)}</span></div>`
            )
          }
        }

        content += details.join('')

        if (showTotal && total > 0) {
          content += '<div class="chart-tooltip-total">'
          content += '<div class="chart-tooltip-total-row">'
          content += `<span>${totalLabel}:</span><strong>${this.formatValue(total, tooltipObj.format)}</strong>`
          content += '</div></div>'
        }

        content += '</div>'
        return content
      }
    }
  },

  /**
   * Process tooltip configuration based on type
   */
  processTooltip(tooltip: unknown): unknown {
    if (!tooltip || typeof tooltip !== 'object') {
      return tooltip
    }

    const tooltipObj = tooltip as {
      type?: string
      trigger?: string
      valueField?: string
      context_details?: Record<string, unknown>
      sections?: Record<string, unknown>[]
      templates?: Record<string, string>
      [key: string]: unknown
    }

    // Handle radial progress tooltip
    if (tooltipObj.type === 'radial_progress') {
      return this.processRadialProgressTooltip(tooltipObj)
    }

    // Handle heatmap with context details
    if (tooltipObj.type === 'heatmap_with_context') {
      return this.processHeatmapWithContextTooltip(tooltipObj)
    }

    // Handle calendar heatmap tooltip with context breakdown
    if (tooltipObj.type === 'calendar_with_context') {
      const contextData = tooltipObj.context_data || {}
      const contextTranslations = tooltipObj.context_translations || {}
      const noActivityText = tooltipObj.no_activity_text || 'No activity'
      const locale = tooltipObj.locale || 'en'

      return {
        ...TOOLTIP_STYLE,
        trigger: tooltipObj.trigger || 'item',
        position: (
          point: number[],
          _params: unknown,
          _dom: HTMLElement,
          _rect: unknown,
          size: { contentSize: number[]; viewSize?: number[] }
        ) => {
          // Smart positioning to avoid covering the hovered cell
          // point[0] is x coordinate, point[1] is y coordinate
          const chartWidth = size.viewSize?.[0] || 800
          const chartHeight = size.viewSize?.[1] || 600
          const tooltipWidth = size.contentSize[0]
          const tooltipHeight = size.contentSize[1]

          // Default offset from the cell
          const offset = 10

          // Position to the right if on left half, to the left if on right half
          let x = point[0]
          let y = point[1] - tooltipHeight - offset

          // If tooltip would go off top, show below
          if (y < 0) {
            y = point[1] + offset
          }

          // If on right side, position to the left of cursor
          if (point[0] > chartWidth / 2) {
            x = point[0] - tooltipWidth - offset
          } else {
            x = point[0] + offset
          }

          // Ensure tooltip stays within bounds
          x = Math.max(0, Math.min(x, chartWidth - tooltipWidth))
          y = Math.max(0, Math.min(y, chartHeight - tooltipHeight))

          return [x, y]
        },
        confine: true,
        textStyle: tooltipObj.textStyle || {
          fontFamily: 'Keruj Sans, system-ui, sans-serif',
          fontSize: 12,
          color: '#1f2937'
        },
        formatter: (params: TooltipFormatterParams) => {
          // Calendar heatmap data format: ["YYYY-MM-DD", value]
          const dataArray = Array.isArray(params.data) ? params.data : []
          const [dateStr, value] = dataArray

          if (!dateStr) {
            return ''
          }

          // Format date nicely with proper locale
          const date = new Date(dateStr)
          let formattedDate: string

          // For Ukrainian, manually format to avoid case issues
          if (locale === 'uk') {
            const weekdays = [
              'Неділя',
              'Понеділок',
              'Вівторок',
              'Середа',
              'Четвер',
              "П'ятниця",
              'Субота'
            ]
            const months = [
              'січня',
              'лютого',
              'березня',
              'квітня',
              'травня',
              'червня',
              'липня',
              'серпня',
              'вересня',
              'жовтня',
              'листопада',
              'грудня'
            ]

            const weekday = weekdays[date.getDay()]
            const day = date.getDate()
            const month = months[date.getMonth()]
            const year = date.getFullYear()

            formattedDate = `${weekday}, ${day} ${month} ${year}`
          } else {
            // For other locales, use standard formatting with capitalization
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }
            const displayLocale = locale === 'en' ? 'en-US' : navigator.language
            formattedDate = date.toLocaleDateString(displayLocale, options)
            // Ensure first letter is capitalized
            formattedDate =
              formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
          }

          // Get context breakdown for this date
          const dayContext = (contextData as Record<string, unknown>)[
            dateStr as string
          ]

          let content = `<div class="chart-tooltip" style="font-family: ${CHART_FONT};">`
          content += `<div class="chart-tooltip-title">${formattedDate}</div>`

          // Add context breakdown if available
          const contextWithBreakdown = dayContext as { breakdown?: unknown[] }
          if (
            contextWithBreakdown?.breakdown &&
            contextWithBreakdown.breakdown.length > 0
          ) {
            content +=
              '<div style="margin: 0; padding: 4px 0; border-top: 1px solid #e5e7eb;"></div>'
            content += '<div class="chart-tooltip-items">'

            for (const item of contextWithBreakdown.breakdown as Record<
              string,
              unknown
            >[]) {
              const contextName = String(item.context || 'unknown')
              const percentage = item.percentage || 0
              const amount = Number.isFinite(item.count)
                ? `${item.count} · ${percentage}%`
                : `${percentage}%`

              // Labels can be tenant-authored (document type titles)
              const label = escapeHtmlText(
                (contextTranslations as Record<string, string>)[contextName] ||
                  contextName
              )

              content += `<div class="chart-tooltip-item" style="display: flex; justify-content: space-between; align-items: center; padding: 2px 0; font-size: 11px;">`
              content += `<span class="chart-tooltip-label" style="text-align: left;">${label}</span>`
              content += `<span class="chart-tooltip-value" style="text-align: right; margin-left: 16px; font-weight: 500;">${amount}</span>`
              content += '</div>'
            }

            content += '</div>'
          } else if (value === 0) {
            // No activity on this day
            content +=
              '<div style="margin: 0; padding: 4px 0; border-top: 1px solid #e5e7eb;"></div>'
            content += '<div class="chart-tooltip-items">'
            content += `<div class="chart-tooltip-item" style="color: #9ca3af;">${noActivityText}</div>`
            content += '</div>'
          }

          content += '</div>'
          return content
        }
      }
    }

    if (tooltipObj.type === 'currency_axis') {
      return this.processCurrencyAxisTooltip(tooltipObj)
    }

    // Handle structured tooltip format
    if (tooltipObj.type === 'structured' && tooltipObj.sections) {
      return {
        ...TOOLTIP_STYLE,
        trigger: tooltipObj.trigger || 'item',
        formatter: (params: TooltipFormatterParams) => {
          let content = `<div class="chart-tooltip" style="font-family: ${CHART_FONT};">`

          // Special handling for heatmap data structure
          if (Array.isArray(params.value) && params.value.length === 3) {
            // This is a heatmap with [x_index, y_index, value]
            const [xIndex, yIndex, value] = params.value

            // Get axis data from the chart
            const chartOption = this.chartInstance?.getOption() || {}
            const optionWithAxes = chartOption as {
              xAxis?: Array<{ data?: unknown[] }>
              yAxis?: Array<{ data?: unknown[] }>
            }
            const xData = optionWithAxes.xAxis?.[0]?.data || []
            const yData = optionWithAxes.yAxis?.[0]?.data || []

            const xValue = xData[xIndex] || xIndex
            const yValue = yData[yIndex] || yIndex

            // Format hour properly if it's a number or 2-digit string
            const formattedHour =
              typeof xValue === 'number'
                ? `${xValue}:00`
                : typeof xValue === 'string' && /^\d{1,2}$/.test(xValue)
                  ? `${xValue}:00`
                  : xValue

            // Display as title
            content += `<div class="chart-tooltip-title">${yValue}, ${formattedHour}</div>`
            content += `<div class="chart-tooltip-subtitle">${heatmapValueLine(tooltipObj, value)}</div>`
          } else {
            // Regular structured tooltip
            for (const section of tooltipObj.sections || []) {
              const value = this.getNestedValue(
                params,
                (section as Record<string, unknown>).path as string[]
              )
              const formattedValue = this.formatValue(
                value,
                (section as Record<string, unknown>).format as string
              )
              content += `<div class="chart-tooltip-row"><span class="chart-tooltip-label">${(section as Record<string, unknown>).label}:</span><span class="chart-tooltip-value">${formattedValue}</span></div>`
            }
          }

          content += '</div>'
          return content
        }
      }
    }

    // Handle stacked bar chart tooltip
    if (tooltipObj.type === 'stacked') {
      return this.processStackedTooltip(tooltipObj)
    }

    // Return unchanged for other tooltip types
    return tooltip
  },

  /**
   * Get nested value from object using path array
   */
  getNestedValue(obj: unknown, path: string[]): unknown {
    if (!(path && Array.isArray(path))) {
      return null
    }

    let value = obj
    for (const key of path) {
      if (value && typeof value === 'object') {
        value = (value as Record<string, unknown>)[key]
      } else {
        return null
      }
    }
    return value
  },

  /**
   * Process calendar configuration to add localized day names
   */

  /**
   * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
   */
  getOrdinal(num: number): string {
    const n = typeof num === 'string' ? Number.parseInt(num, 10) : num
    if (Number.isNaN(n)) {
      return String(num)
    }

    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  },

  /**
   * Format value based on format type
   */
  formatValue(value: unknown, format?: string): string {
    if (value === null || value === undefined) {
      return '-'
    }

    switch (format) {
      case 'number':
        return typeof value === 'number'
          ? value.toLocaleString()
          : String(value)
      case 'percentage':
        return typeof value === 'number'
          ? `${value.toFixed(1)}%`
          : String(value)
      case 'time_hour':
        return `${value}:00`
      default:
        return String(value)
    }
  },

  /**
   * Detect locale for chart from tooltip or document
   */
  detectChartLocale(): string {
    // Try to get locale from the chart spec tooltip
    const chartEl = this.el
    const specString = chartEl?.dataset?.spec

    if (specString) {
      try {
        const spec = JSON.parse(specString)
        if (spec?.tooltip?.locale) {
          return spec.tooltip.locale
        }
      } catch (_e) {
        // Ignore parse errors
      }
    }

    // Try to get from document lang attribute
    const docLang = document.documentElement.lang
    if (docLang) {
      return docLang
    }

    // Default to browser locale
    return navigator.language || 'en'
  },

  showError(message: string, error?: unknown, title?: string) {
    // Push error to server for tracking - use pushAndCall to ensure delivery
    pushAndCall(
      this,
      'chart_error',
      {
        id: this.el.id,
        message: message,
        error: (error as { message?: unknown })?.message || String(error)
      },
      () => {}, // No-op callback
      undefined,
      'Failed to report chart error'
    )

    // Show user-friendly error message
    const errorDetails = (error as { message?: unknown })?.message || ''
    const errorTitle = title || this.el.dataset.errorTitle || 'Chart Error'
    this.el.innerHTML = `
      <div class="flex items-center justify-center h-full p-4">
        <div class="text-center max-w-md">
          <div class="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-red-100 text-red-600">
            ${createIcon('warning-circle', 'regular', 'xl').outerHTML}
          </div>
          <h3 class="mb-2 text-lg font-semibold text-gray-900">${errorTitle}</h3>
          <p class="mb-2 text-sm text-gray-600">${message}</p>
          ${errorDetails ? `<p class="text-xs text-gray-500 font-mono">${errorDetails}</p>` : ''}
        </div>
      </div>
    `
  },

  // Store references
  chartInstance: null as ECharts | null,
  resizeObserver: null as ResizeObserver | null
}

// Export alias for backwards compatibility
export const Chart = FacadeChart
